import Decimal from 'decimal.js';
import { Expense, Session, Province, PROVINCES } from '../types/models';
import { TaxEngine } from './taxEngine';

// Configure Decimal.js for financial calculations
Decimal.config({ precision: 10, rounding: Decimal.ROUND_HALF_UP });

export class GSTCalculator {
  private province: Province;
  private taxEngine: TaxEngine;

  constructor(provinceCode: string) {
    const province = PROVINCES.find(p => p.code === provinceCode);
    if (!province) {
      throw new Error(`Invalid province code: ${provinceCode}`);
    }
    this.province = province;
    this.taxEngine = new TaxEngine(provinceCode);
  }

  /**
   * Calculate GST/HST from a pre-tax amount
   */
  calculateGSTFromPreTax(preTaxAmount: number): number {
    return this.taxEngine.calculateGSTFromPreTax(preTaxAmount);
  }

  /**
   * Calculate GST/HST from a total amount (tax-inclusive)
   */
  calculateGSTFromTotal(totalAmount: number): number {
    return this.taxEngine.calculateGSTFromTotal(totalAmount);
  }

  /**
   * Calculate pre-tax amount from total (tax-inclusive)
   */
  calculatePreTaxFromTotal(totalAmount: number): number {
    return this.taxEngine.calculatePreTaxFromTotal(totalAmount);
  }

  /**
   * Calculate claimable ITC for an expense
   */
  calculateClaimableITC(expense: Expense): number {
    return this.taxEngine.calculateClaimableITC(expense);
  }

  /**
   * Calculate deductible amount for income tax purposes
   */
  calculateDeductibleAmount(expenseAmount: number, businessUsePct: number): number {
    return this.taxEngine.calculateDeductibleAmount(expenseAmount, businessUsePct);
  }

  /**
   * Get the applicable tax rate for the province
   */
  private getTaxRate(): number {
    return this.taxEngine.getTaxRate();
  }

  /**
   * Get tax type name (GST or HST)
   */
  getTaxTypeName(): string {
    return this.taxEngine.getTaxSchemeName();
  }

  /**
   * Get the tax rate as a percentage
   */
  getTaxRatePercent(): number {
    return this.taxEngine.getTaxRatePercent();
  }
}

export class CRALineCalculator {
  /**
   * Calculate all CRA GST/HST return lines
   * @deprecated Use CRALineCalculator from taxEngine.ts instead
   */
  static calculateGSTLines(
    sessions: Session[],
    expenses: Expense[],
    provinceCode: string
  ): {
    line90: number;
    line91: number;
    line101: number;
    line103: number;
    line106: number;
    line108: number;
    line109: number;
    line115: number;
  } {
    // Delegate to the new tax engine implementation
    const { CRALineCalculator: NewCRALineCalculator } = require('./taxEngine');
    return NewCRALineCalculator.calculateGSTLines(sessions, expenses, provinceCode);
  }

  /**
   * Calculate income tax deductions summary
   * @deprecated Use CRALineCalculator from taxEngine.ts instead
   */
  static calculateIncomeTaxSummary(
    sessions: Session[],
    expenses: Expense[],
    provinceCode: string
  ): {
    totalIncome: number;
    totalExpenses: number;
    totalDeductions: number;
    netIncome: number;
  } {
    // Delegate to the new tax engine implementation
    const { CRALineCalculator: NewCRALineCalculator } = require('./taxEngine');
    return NewCRALineCalculator.calculateIncomeTaxSummary(sessions, expenses, provinceCode);
  }
}

/**
 * Utility functions for common GST calculations
 * @deprecated Use taxUtils from taxEngine.ts instead
 */
export const gstUtils = {
  /**
   * Format currency for display
   */
  formatCurrency(amount: number): string {
    const { taxUtils } = require('./taxEngine');
    return taxUtils.formatCurrency(amount);
  },

  /**
   * Format percentage for display
   */
  formatPercentage(percentage: number): string {
    const { taxUtils } = require('./taxEngine');
    return taxUtils.formatPercentage(percentage);
  },

  /**
   * Round to 2 decimal places for currency
   */
  roundCurrency(amount: number): number {
    const { taxUtils } = require('./taxEngine');
    return taxUtils.roundCurrency(amount);
  },

  /**
   * Calculate percentage of total
   */
  calculatePercentage(part: number, total: number): number {
    const { taxUtils } = require('./taxEngine');
    return taxUtils.calculatePercentage(part, total);
  },

  /**
   * Validate GST number format (9 digits)
   */
  validateGSTNumber(gstNumber: string): boolean {
    const { taxUtils } = require('./taxEngine');
    return taxUtils.validateGSTNumber(gstNumber);
  },

  /**
   * Format GST number for display
   */
  formatGSTNumber(gstNumber: string): string {
    const { taxUtils } = require('./taxEngine');
    return taxUtils.formatGSTNumber(gstNumber);
  },
};
